/**
 * Зачет матча в статистике профиля — одно правило на оба пути, которыми она
 * ведется: вживую (finalizeGame у сетевого матча, сохранение итогов у
 * локальной партии) и пересчетом на старте сервера
 * (server/utils/stats/accumulateUserStatistics.js). Пути обязаны давать одно и
 * то же, поэтому и спрашивают об этом в одном месте.
 */

// Идентификатор фракции уходит в путь поля базы (factionsStatistics.<id>…), а у
// локальной партии он приходит из тела запроса: точка или «$» в нем завели бы
// посторонние поля. Настоящие идентификаторы фракций — латиница с подчеркиванием
const STATISTICS_FACTION_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
// Настоящие идентификаторы — одно-два слова; длиннее этого фракций не бывает
const STATISTICS_FACTION_ID_MAX_LENGTH = 60;
// Имена, которые в объекте JS значат не «ключ»: пересчет копит статистику в
// обычном объекте, и запись по такому имени подменила бы ему прототип, тогда
// как база завела бы обычное поле — два пути разошлись бы
const STATISTICS_FACTION_ID_RESERVED = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Засчитывается ли участнику записи матча эта партия: счетчики игр, отклонение
 * от медианного места, статистика фракций, задание за бой с ботами.
 *
 * Не засчитывается:
 *  - участнику без пользователя (у бота локальной партии и у «явного» бота в
 *    playerId свой id, аккаунта за ними нет);
 *  - «явному» боту рейтингового матча (см. src/configs/visibleRankedBots.js):
 *    в старых записях он еще носил id аккаунта пула;
 *  - боту ЛОКАЛЬНОЙ партии, на котором стоит userId. Так выглядит партия из
 *    одних ботов (режим зрителя или все места отданы компьютеру): владелец ее
 *    только смотрел, а его userId сервер ставит одному из ботов, чтобы партия
 *    осталась в его истории. Для зачета это не участие. В сетевом матче бот с
 *    userId — неявный бот пула, настоящий аккаунт, и ему матч засчитывается.
 *
 * @param {{isLocal?: boolean}} match - запись матча
 * @param {{userId?: *, isBot?: boolean, isVisibleBot?: boolean}} player - участник из match.players
 * @returns {boolean}
 */
export const checkIfMatchCountedForPlayer = (match, player) => Boolean(
  player
  && player.userId
  && !player.isVisibleBot
  && !(match?.isLocal && player.isBot)
);

/**
 * Фракция участника для статистики фракций; null — считать нечего (фракции нет
 * или идентификатор не годится в имя поля).
 *
 * @param {{factionId?: string}} player
 * @returns {string|null}
 */
export const getStatisticsFactionId = (player) => {
  const factionId = player?.factionId;
  const isUsable = typeof factionId === 'string'
    && factionId.length <= STATISTICS_FACTION_ID_MAX_LENGTH
    && STATISTICS_FACTION_ID_PATTERN.test(factionId)
    && !STATISTICS_FACTION_ID_RESERVED.has(factionId);
  return isUsable ? factionId : null;
};
